import { Injectable } from '@angular/core';
import { Subscription, combineLatest } from 'rxjs';

import { BrowseTypesEnum } from '../models/browse-types.enum';
import { BrowseFilter } from '../models/filter';
import { BrowseSorting } from '../models/sorting';
import { Sport } from '../models/sport';
import { CoachingStore } from '../store/coaching/coaching.store';
import { FavoriteStore } from '../store/favorite/favorite.store';
import { FilterStore } from '../store/filter/filter.store';
import { GpsStore } from '../store/gps/gps.store';
import { MostSearchedTermsStore } from '../store/most-searched-terms/most-searched-terms.store';
import { PlaylistStore } from '../store/playlist/playlist.store';
import { SortingStore } from '../store/sorting/sorting.store';
import { SportStore } from '../store/sport/sport.store';
import { VideoStore } from '../store/video/video.store';
import { WorkoutStore } from '../store/workout/workout.store';

@Injectable({
  providedIn: 'root',
})
export class CatalogService {
  private subscriptions: Subscription[];

  constructor(
    private sportStore: SportStore,
    private filterStore: FilterStore,
    private sortingStore: SortingStore,
    private videoStore: VideoStore,
    private playlistStore: PlaylistStore,
    private gpsStore: GpsStore,
    private workoutStore: WorkoutStore,
    private coachingStore: CoachingStore,
    private mostSearchedTermsStore: MostSearchedTermsStore,
    private favoriteStore: FavoriteStore,
  ) {
    this.subscriptions = [];
  }
  
  subscribeToFetch(): void {
    this.mostSearchedTermsStore.getMostSearchedTerms();
    this.subscriptions.push(
      this.subscriptionToFetchHomePageContentAndFavoritesWhenSportChanges(),
      this.subscriptionToFetchCatalogWhenSportFiltersOrSortingChange('video'),
      this.subscriptionToFetchCatalogWhenSportFiltersOrSortingChange(
        'playlist'
      ),
      this.subscriptionToFetchCatalogWhenSportFiltersOrSortingChange('gps'),
      this.subscriptionToFetchCatalogWhenSportFiltersOrSortingChange('workout'),
      this.subscriptionToFetchCatalogWhenSportFiltersOrSortingChange('coaching')
    );
  }

  unsubscribeToFetch(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    this.subscriptions = [];
  }

  private subscriptionToFetchHomePageContentAndFavoritesWhenSportChanges(): Subscription {
    return this.sportStore.selectedSport$.subscribe((selectedSport) => {
      this.videoStore.getAllHomeVideos(selectedSport);
      this.playlistStore.getHomePlaylists(selectedSport);

      for (const browseType in BrowseTypesEnum) {
        const contentType: BrowseTypesEnum = BrowseTypesEnum[browseType];
        this.favoriteStore.getFavorites({
          newSelectedSport: selectedSport,
          nextPage: false,
          contentType,
        });
      }
    });
  }

  private subscriptionToFetchCatalogWhenSportFiltersOrSortingChange(
    dataType: 'video' | 'playlist' | 'gps' | 'workout' | 'coaching'
  ): Subscription {
    const obj = {
      video: {
        filters: this.filterStore.videoFilters$,
        sortings: this.sortingStore.videoSorting$,
        method: this.videoStore.getVideos.bind(this.videoStore),
      },
      playlist: {
        filters: this.filterStore.playlistFilters$,
        sortings: this.sortingStore.playlistSorting$,
        method: this.playlistStore.getPlaylists.bind(this.playlistStore),
      },
      gps: {
        filters: this.filterStore.gpsFilters$,
        sortings: this.sortingStore.gpsSorting$,
        method: this.gpsStore.getGpsTracks.bind(this.gpsStore),
      },
      workout: {
        filters: this.filterStore.workoutFilters$,
        sortings: this.sortingStore.workoutSorting$,
        method: this.workoutStore.getWorkouts.bind(this.workoutStore),
      },
      coaching: {
        filters: this.filterStore.coachingFilters$,
        sortings: this.sortingStore.coachingSorting$,
        method: this.coachingStore.getCoachings.bind(this.coachingStore),
      },
    };

    return combineLatest([
      this.sportStore.selectedSport$,
      obj[dataType].filters,
      obj[dataType].sortings,
    ]).subscribe((res: [Sport, BrowseFilter[], BrowseSorting]) => {
      const selectedSport: Sport = res[0];
      const filters: BrowseFilter[] = res[1];
      const sorting: BrowseSorting = res[2];

      obj[dataType].method({
        newSelectedSport: selectedSport,
        newFilters: filters,
        newSorting: sorting,
        nextPage: false,
      });
    });
  }
}
